/**
 * Static portfolio data – each project follows the Mediverse-style detail structure.
 * Use placeholder images from Unsplash/Pexels for now.
 */

export const PORTFOLIO_PROJECTS = [
  {
    id: "mediverse",
    title: "Mediverse",
    tagline: "Medical Social Networking Platform (iOS & Android)",
    role: "Lead React Native Developer",
    client: "Healthcare Technology Startup (USA)",
    duration: "8 Months",
    techStack: "React Native CLI, Node.js, Firebase, Socket.IO",
    viewAppUrl: "https://play.google.com/store",
    banner: "/assets/images/testimonial/mediverse_main.png",
    mockup: "/assets/images/iphone.png",
    screenshots: [
      "/assets/images/testimonial/mediverse/1.webp",
      "/assets/images/testimonial/mediverse/2.webp",
      "/assets/images/testimonial/mediverse/3.webp",
      "/assets/images/testimonial/mediverse/4.webp",
      "/assets/images/testimonial/mediverse/5.webp",
      "/assets/images/testimonial/mediverse/6.webp",
      "/assets/images/testimonial/mediverse/7.webp",
      "/assets/images/testimonial/mediverse/8.webp",
      "/assets/images/testimonial/mediverse/9.webp"
    ],
    showcaseImage: "/assets/images/testimonial/mediverse/showcase.webp",
    overview: "Mediverse is a large-scale medical social networking platform designed to connect healthcare professionals across different career stages - from students to senior doctors. The platform combines professional networking, community engagement, real-time messaging, and event management into a single scalable mobile product.",
    keyFeatures: [
      {
        title: "Authentication & Security",
        icon: "ti-lock",
        points: ["Google & Apple sign-in", "Secure password recovery", "Role-based access control"]
      },
      {
        title: "Groups & Communities",
        icon: "ti-user",
        points: ["Public & private groups", "Admin moderation controls", "Posts, polls & events inside groups"]
      },
      {
        title: "Content System",
        icon: "ti-layout-grid2",
        points: ["Rich media posts (images, video, GIFs)", "Mentions & link previews", "Admin content moderation tools"]
      },
      {
        title: "Business Pages",
        icon: "ti-briefcase",
        points: ["Professional business profiles", "Verification workflow", "Integrated groups & events"]
      },
      {
        title: "Events",
        icon: "ti-calendar",
        points: ["Virtual & in-person events", "Role-based visibility", "Calendar integration"]
      },
      {
        title: "Chat & Messaging",
        icon: "ti-comments",
        points: ["One-to-one & group chat", "Media & link sharing", "Message delivery indicators"]
      }
    ],
    technicalApproach: [
      {
        title: "React Native CLI",
        description: "Chosen for long-term flexibility and full native control over mobile performance."
      },
      {
        title: "TanStack Query",
        description: "Used for efficient API state management, caching, and offline-first support."
      },
      {
        title: "Socket.IO & Firebase",
        description: "Implemented real-time messaging, live updates, analytics, and push notifications."
      }
    ],
    challengesSolved: [
      "Handling real-time updates without UI lag",
      "Managing complex role-based permission systems",
      "Optimizing performance for media-heavy content feeds",
      "Maintaining stability across iOS and Android",
      "Building scalable architecture for future growth"
    ],
    responsibilities: [
      "Designed scalable mobile architecture",
      "Implemented core social networking features",
      "Integrated real-time communication systems",
      "Ensured cross-platform performance parity",
      "Integrated analytics and crash monitoring",
      "Delivered production-ready mobile applications"
    ],
    outcome: [
      {
        title: "Live on iOS & Android",
        description: "Successfully launched on both App Store and Google Play.",
        icon: "ti-mobile"
      },
      {
        title: "Stable Real-Time Communication",
        description: "Reliable instant messaging and content feed updates.",
        icon: "ti-signal"
      },
      {
        title: "Scalable Architecture",
        description: "Ready for user growth and new features.",
        icon: "ti-cloud-up"
      },
      {
        title: "Production-Ready Delivery",
        description: "Successfully launched high-quality applications to production.",
        icon: "ti-check-box"
      }
    ],
    finalNote: "Mediverse connects the global medical community through a high-performance, real-time social platform built for scale.",
    workTypeName: "Mobile Apps"
  },

{
  id: "joylo",
  title: "Joylo",
  tagline: "Multi-Vendor Commerce & Delivery Ecosystem",

  role: "Senior React Native Mobile App Developer",
  client: "E-Commerce & Logistics Startup",
  duration: "4 Months",
  techStack: "React Native (Expo), Node.js, Firebase, Socket.IO",

  viewAppUrl: "https://play.google.com/store",
  androidlink: "https://play.google.com/store",
  ioslink: "https://apps.apple.com/",

    banner: "/assets/images/testimonial/joylo_main.webp",
    mockup: "/assets/images/iphone.png",
    screenshots: [
      "/assets/images/testimonial/joylo/1.webp",
      "/assets/images/testimonial/joylo/2.webp",
      "/assets/images/testimonial/joylo/3.webp",
      "/assets/images/testimonial/joylo/4.webp",
      "/assets/images/testimonial/joylo/5.webp",
      "/assets/images/testimonial/joylo/6.webp",
      "/assets/images/testimonial/joylo/7.webp"
    ],
    showcaseImage: "/assets/images/testimonial/joylo/showcase.webp",


  overview:
    "Joylo is a full-scale multi-vendor commerce and last-mile delivery platform connecting customers, stores, and delivery riders through three dedicated mobile applications. The ecosystem enables end-to-end digital commerce - from product discovery and payment to real-time delivery tracking - built for scalability, high transaction volume, and real-time coordination across multiple stakeholders.",

  keyFeatures: [
    {
      title: "Customer App",
      icon: "ti-shopping-cart",
      points: [
        "Product discovery with search and filters",
        "Cart management and secure checkout",
        "Address selection with map pin",
        "Apple Pay & Google Pay integration",
        "Real-time order tracking and history",
        "In-app chat with stores and riders",
        "Multi-language and dark/light mode",
      ],
    },
    {
      title: "Store App",
      icon: "ti-store",
      points: [
        "Store availability (online/offline)",
        "Instant order alerts with sound notifications",
        "Order accept/reject with OTP confirmation",
        "Product management (add/edit/delete)",
        "Order completion tracking",
        "Date-wise earnings reports",
        "In-app operational chat",
      ],
    },
    {
      title: "Rider App",
      icon: "ti-truck",
      points: [
        "Rider availability toggle",
        "Order alerts and acceptance",
        "Store & customer navigation",
        "OTP-based secure handoff",
        "Delivery completion confirmation",
        "Completed orders & earnings overview",
        "In-app chat with stores and customers",
      ],
    },
  ],

  technicalApproach: [
    {
      title: "Multi-App React Native Architecture",
      description:
        "Built three interconnected mobile applications sharing reusable logic while maintaining role-based behaviors for customers, stores, and riders.",
    },
    {
      title: "Real-Time Communication",
      description:
        "Socket.IO enabled live order coordination, chat, and status updates across all apps.",
    },
    {
      title: "Background Services & Notifications",
      description:
        "Firebase Cloud Messaging and background location tracking ensured reliable delivery workflows even in kill-mode.",
    },
  ],

  challengesSolved: [
    "Achieved reliable background location tracking on iOS and Android",
    "Prevented notification loss during app state changes",
    "Ensured stable real-time chat under concurrent users",
    "Managed payment flows without blocking order pipelines",
    "Maintained performance across three apps sharing common logic",
    "Reduced operational friction for stores and riders",
  ],

  responsibilities: [
    "End-to-end development of all three mobile applications",
    "Architected reusable logic across apps with role-based behavior",
    "Implemented payments, real-time chat, and live order tracking",
    "Integrated background location tracking and notifications",
    "Managed App Store & Play Store deployments",
    "Ensured stability and performance in production",
  ],

  outcome: [
    {
      title: "Production-Ready Multi-App Ecosystem",
      description:
        "Successfully delivered three interconnected applications for customers, stores, and riders.",
      icon: "ti-mobile",
    },
    {
      title: "Real-Time Commerce Operations",
      description:
        "Enabled seamless coordination between ordering, fulfillment, and delivery.",
      icon: "ti-signal",
    },
    {
      title: "Scalable Platform Foundation",
      description:
        "Built architecture ready for vendor and rider growth.",
      icon: "ti-cloud-up",
    },
    {
      title: "Reliable App Store Deployment",
      description:
        "Ensured smooth publishing and long-term production stability.",
      icon: "ti-check-box",
    },
  ],

  finalNote:
    "Joylo demonstrates my ability to build complex real-world commerce systems where performance, reliability, and coordination drive business success.",

  workTypeName: "Mobile Apps",
},

{
  id: "scorehub",
  title: "ScoreHub",
  tagline: "Community-Driven Sports Prediction & Live Score Platform",

  role: "React Native Application Developer",
  client: "Sports & Fan Engagement Platform",
  duration: "5 Months",
  techStack: "React Native, Socket.IO, Apollo Client, Redux Toolkit",
  androidlink: "https://play.google.com/store",
  ioslink: "https://apps.apple.com/",

    banner: "/assets/images/testimonial/scorehub_main.jpeg",
    mockup: "/assets/images/iphone.png",
    screenshots: [
      "/assets/images/testimonial/scorehub/1.webp",
      "/assets/images/testimonial/scorehub/2.webp",
      "/assets/images/testimonial/scorehub/3.webp",
      "/assets/images/testimonial/scorehub/4.webp",
      "/assets/images/testimonial/scorehub/5.webp",
      "/assets/images/testimonial/scorehub/6.webp",
      "/assets/images/testimonial/scorehub/7.webp",
      "/assets/images/testimonial/scorehub/8.webp",
      "/assets/images/testimonial/scorehub/9.webp",
      "/assets/images/testimonial/scorehub/10.webp"
    ],
    showcaseImage: "/assets/images/testimonial/scorehub/showcase.webp",
  overview:
    "ScoreHub is a modern sports prediction and live-score platform that blends real-time match data, community predictions, and competitive leaderboards into a single mobile experience. Users predict match outcomes, earn points based on accuracy, and compete globally - turning passive sports viewing into interactive competition.",

  keyFeatures: [
    {
      title: "Authentication & Onboarding",
      icon: "ti-id-badge",
      points: [
        "Email & Google authentication",
        "Profile setup with favorite teams",
        "Guided onboarding for predictions",
        "Password recovery flows",
      ],
    },
    {
      title: "Prediction & Community Feed",
      icon: "ti-comments",
      points: [
        "Community prediction feeds",
        "Like, comment, and share predictions",
        "League-based filtering",
        "Personalized 'For You' feed",
      ],
    },
    {
      title: "Live Match & Scores",
      icon: "ti-pulse",
      points: [
        "Live score widgets",
        "Real-time match statistics",
        "League and date-based filtering",
        "Match detail insights and lineups",
      ],
    },
    {
      title: "Leaderboards",
      icon: "ti-crown",
      points: [
        "Global and friends ranking",
        "Points and accuracy modes",
        "Weekly and monthly boards",
        "Personal rank highlighting",
      ],
    },
    {
      title: "Prediction System",
      icon: "ti-target",
      points: [
        "Multi-type match predictions",
        "Stacked predictions with multipliers",
        "Editable predictions before kickoff",
        "Progress badges and feedback",
      ],
    },
    {
      title: "Predictor Profiles",
      icon: "ti-user",
      points: [
        "Public predictor profiles",
        "Accuracy stats and badges",
        "Prediction history and filters",
        "Follow other predictors",
      ],
    },
  ],

  technicalApproach: [
    {
      title: "Cross-Platform React Native",
      description:
        "Ensured consistent performance and UI across iOS and Android.",
    },
    {
      title: "Real-Time Data Handling",
      description:
        "Socket.IO integration for live match updates and prediction syncing.",
    },
    {
      title: "State & UI Optimization",
      description:
        "Redux Toolkit managed complex filters, sorting, and heavy UI states.",
    },
  ],

  challengesSolved: [
    "Managed real-time UI updates without performance degradation",
    "Prevented feed re-render issues during live matches",
    "Designed intuitive filters for complex datasets",
    "Balanced data density with usability",
    "Ensured smooth transitions between live and static content",
    "Maintained prediction integrity and edit rules",
  ],

  responsibilities: [
    "Implemented core application flows and screens",
    "Built prediction, leaderboard, and feed experiences",
    "Integrated real-time updates via sockets",
    "Managed complex UI state with filters and sorting",
    "Ensured smooth performance during live matches",
    "Supported onboarding and profile systems",
  ],

  outcome: [
    {
      title: "High-Engagement Platform",
      description:
        "Delivered an interactive sports prediction experience.",
      icon: "ti-mobile",
    },
    {
      title: "Daily User Retention",
      description:
        "Enabled competition and ranking-based engagement.",
      icon: "ti-stats-up",
    },
    {
      title: "Scalable Foundation",
      description:
        "Architecture ready for future leagues and sports.",
      icon: "ti-cloud-up",
    },
    {
      title: "Community-Centric Experience",
      description:
        "Successfully blended live data with social interaction.",
      icon: "ti-heart",
    },
  ],

  finalNote:
    "ScoreHub bridges live sports data with community-driven prediction - delivering a fast, engaging, and scalable fan experience.",

  workTypeName: "Mobile Apps",
}, 
{
  id: "beyond-abundance",
  title: "Beyond Abundance",
  tagline: "Personal Development & Goal Execution Platform",

  role: "React Native Application Developer",
  client: "Coaching & Personal Growth Platform",
  duration: "6 Months",
  techStack: "React Native (Expo), Node.js, Firebase, Redux Saga",

    banner: "/assets/images/testimonial/ba_main.webp",
    mockup: "/assets/images/iphone.png",
     screenshots: [
      "/assets/images/testimonial/ba/1.png",
      "/assets/images/testimonial/ba/2.png",
      "/assets/images/testimonial/ba/3.png",
      "/assets/images/testimonial/ba/4.png",
      "/assets/images/testimonial/ba/5.png",
      "/assets/images/testimonial/ba/6.png",
      "/assets/images/testimonial/ba/7.png",
      "/assets/images/testimonial/ba/8.png",
      "/assets/images/testimonial/ba/9.png",
      "/assets/images/testimonial/ba/10.png",
      "/assets/images/testimonial/ba/11.png",
      "/assets/images/testimonial/ba/12.png"
    ],
    showcaseImage: "/assets/images/testimonial/ba/showcase.jpeg",

  overview:
    "Beyond Abundance is a comprehensive personal development platform helping individuals and teams design meaningful journeys, build habits, track progress, and achieve long-term goals. It combines structured journeys, smart actions, journaling, and team accountability into one scalable mobile experience used in real-world coaching programs.",

  keyFeatures: [
    {
      title: "Journey Management",
      icon: "ti-map",
      points: [
        "Create and manage personal journeys",
        "Custom, team, and guided journeys",
        "Join journeys via share codes",
        "Publish and purchase journeys",
      ],
    },
    {
      title: "Journaling & Notepad",
      icon: "ti-write",
      points: [
        "Daily journaling with rich text",
        "Media artifacts (image, audio, video)",
        "Web links and formatted notes",
        "Structured thinking tools",
      ],
    },
    {
      title: "Smart Action Engine",
      icon: "ti-target",
      points: [
        "Habit, target, and one-time actions",
        "Custom recurrence scheduling",
        "Progress tracking and graphs",
        "Celebration animations",
      ],
    },
    {
      title: "Teams & Collaboration",
      icon: "ti-users",
      points: [
        "Create and manage teams",
        "Role-based team access",
        "Shared journeys and accountability",
        "Invite and manage members",
      ],
    },
    {
      title: "Notifications & Engagement",
      icon: "ti-bell",
      points: [
        "Custom push notifications",
        "Snooze actions from notification tray",
        "Scheduled reminders",
        "Firebase + local coordination",
      ],
    },
    {
      title: "Payments & Monetization",
      icon: "ti-credit-card",
      points: [
        "Journey purchases via Stripe",
        "Paid content access control",
        "Stable purchase flows",
      ],
    },
  ],

  technicalApproach: [
    {
      title: "Modular Feature Architecture",
      description:
        "Designed a scalable feature-based structure for long-term growth.",
    },
    {
      title: "Complex State Management",
      description:
        "Redux Toolkit + Redux Saga handled async flows and persistent state.",
    },
    {
      title: "Media & Notification Handling",
      description:
        "Implemented rich media pipelines and custom notification logic.",
    },
  ],

  challengesSolved: [
    "Managed very large global state without instability",
    "Ensured smooth media handling across devices",
    "Prevented notification conflicts with snooze logic",
    "Maintained performance with heavy journaling usage",
    "Enabled flexible journey design without hardcoding flows",
    "Reduced friction in habit and action tracking",
  ],

  responsibilities: [
    "Built and maintained core mobile features",
    "Implemented journey creation and action tracking",
    "Integrated rich media capture and playback",
    "Managed complex async workflows",
    "Implemented custom notifications and snooze actions",
    "Ensured cross-platform stability",
  ],

  outcome: [
    {
      title: "Live on iOS & Android",
      description:
        "Successfully launched on both App Store and Play Store.",
      icon: "ti-mobile",
    },
    {
      title: "Production-Grade Platform",
      description:
        "Delivered a stable personal growth ecosystem.",
      icon: "ti-check-box",
    },
    {
      title: "Team Accountability Enabled",
      description:
        "Supported real-world coaching and team collaboration.",
      icon: "ti-users",
    },
    {
      title: "Scalable Growth Foundation",
      description:
        "Built ready for future journeys and features.",
      icon: "ti-cloud-up",
    },
  ],

  finalNote:
    "Beyond Abundance transforms human growth workflows into scalable, reliable mobile systems users depend on daily.",

  workTypeName: "Mobile Apps",
},
{
  id: "minutes-ai",
  title: "Minutes AI",
  tagline: "AI-Powered Meeting Notes & Audio Intelligence App",

  role: "React Native Application Developer",
  client: "AI Productivity Startup",
  duration: "4 Months",
  techStack: "React Native (Expo), Firebase, Expo AV, AI APIs",

    banner: "/assets/images/testimonial/minutes_ai_main.webp",
    mockup: "/assets/images/iphone.png",
    screenshots: [
      "/assets/images/testimonial/minutes/1.webp",
      "/assets/images/testimonial/minutes/2.webp",
      "/assets/images/testimonial/minutes/3.webp",
      "/assets/images/testimonial/minutes/4.webp",
      "/assets/images/testimonial/minutes/5.webp",
      "/assets/images/testimonial/minutes/6.webp",
      "/assets/images/testimonial/minutes/7.webp",
      "/assets/images/testimonial/minutes/8.webp",
    ],
    showcaseImage: "/assets/images/testimonial/minutes/showcase.webp",

  overview:
    "Minutes AI is an AI-powered meeting assistant that records, transcribes, summarizes, and organizes meetings automatically. It converts long audio sessions into structured minutes, searchable transcripts, summaries, and AI-powered Q&A - eliminating manual note-taking for professionals and teams.",

  keyFeatures: [
    {
      title: "Recording & Input",
      icon: "ti-microphone",
      points: [
        "Record long audio sessions",
        "Upload audio files",
        "Convert YouTube videos to minutes",
        "Unified recordings dashboard",
      ],
    },
    {
      title: "AI Minutes & Transcript",
      icon: "ti-agenda",
      points: [
        "AI-generated meeting summaries",
        "Searchable transcripts",
        "Structured minutes with action points",
        "Export and share options",
      ],
    },
    {
      title: "AI Chat Assistant",
      icon: "ti-comment-alt",
      points: [
        "Ask questions about meetings",
        "Context-aware AI responses",
        "Persistent chat history per meeting",
      ],
    },
    {
      title: "Background Processing",
      icon: "ti-reload",
      points: [
        "Continuous recording in background",
        "Background uploads to Firebase",
        "State recovery on return",
      ],
    },
    {
      title: "Playback & Controls",
      icon: "ti-control-play",
      points: [
        "Persistent bottom audio player",
        "Playback speed control",
        "Seek and jump controls",
      ],
    },
    {
      title: "Subscriptions & Account",
      icon: "ti-lock",
      points: [
        "Subscription management",
        "Restore purchases",
        "Account deletion & security controls",
      ],
    },
  ],

  technicalApproach: [
    {
      title: "Background-Safe Architecture",
      description:
        "Implemented background recording and uploads without data loss.",
    },
    {
      title: "Media Reliability",
      description:
        "Expo AV and File System ensured stable long-session audio handling.",
    },
    {
      title: "Scalable Firebase Backend",
      description:
        "Firestore structured storage for transcripts and minutes.",
    },
  ],

  challengesSolved: [
    "Ensured uninterrupted background recording",
    "Prevented data loss during long uploads",
    "Managed large transcripts without UI lag",
    "Balanced AI power with simple UX",
    "Reduced friction in subscriptions and accounts",
    "Maintained performance across long audio sessions",
  ],

  responsibilities: [
    "Built recording, upload, and playback flows",
    "Implemented background recording and uploads",
    "Designed minutes, transcript, and AI chat views",
    "Integrated subscription systems",
    "Ensured data safety and performance",
    "Supported production Android & iOS builds",
  ],

  outcome: [
    {
      title: "Launched on Play Store",
      description:
        "Delivered a production-ready AI productivity tool.",
      icon: "ti-mobile",
    },
    {
      title: "High User Trust",
      description:
        "Reliable background recording and processing.",
      icon: "ti-shield",
    },
    {
      title: "Scalable AI Foundation",
      description:
        "Ready for future AI enhancements.",
      icon: "ti-cloud-up",
    },
    {
      title: "Meeting Productivity Boost",
      description:
        "Users focus on meetings - not manual notes.",
      icon: "ti-light-bulb",
    },
  ],

  finalNote:
    "Minutes AI showcases building AI-driven, media-heavy mobile apps where reliability and trust are essential.",

  workTypeName: "Mobile Apps",
},
{
  id: "kupenda",
  title: "Kupenda",
  tagline: "Game-Based Dating & Social Connection App",

  role: "React Native Application Developer",
  client: "Social & Dating Platform",
  duration: "8 Months",
  techStack: "React Native, Sendbird, Firebase, Track Player",

  banner: "/assets/images/testimonial/kupenda_main.png",
  mockup: "/assets/images/iphone.png",
  screenshots: [
      "/assets/images/testimonial/kupenda/1.webp",
      "/assets/images/testimonial/kupenda/2.webp",
      "/assets/images/testimonial/kupenda/3.webp",
      "/assets/images/testimonial/kupenda/4.webp",
      "/assets/images/testimonial/kupenda/5.webp",
    ],
    showcaseImage: "/assets/images/testimonial/kupenda/showcase.webp",

  overview:
    "Kupenda is a modern dating application designed to build deeper connections through game-based interactions, audio communication, and expressive profiles. Instead of relying on text and swipes alone, Kupenda introduces playful activities, audio bios, and interactive games that help users communicate naturally and meaningfully.",

  keyFeatures: [
    {
      title: "Matching & Discovery",
      icon: "ti-search",
      points: [
        "Profile-based user matching",
        "Mutual match system",
        "Match progress visualization",
        "Location-based discovery",
        "Points & rewards for engagement",
      ],
    },
    {
      title: "Profile & Expression",
      icon: "ti-user",
      points: [
        "Image-based user profiles",
        "Recordable audio bios",
        "Audio playback on profiles",
        "Animated match progress indicators",
      ],
    },
    {
      title: "Game-Based Interaction",
      icon: "ti-game",
      points: [
        "Interactive ice-breaker games",
        "Gameplay between matched users",
        "Rewards tied to game progress",
        "Animated visual feedback",
      ],
    },
    {
      title: "Chat & Communication",
      icon: "ti-comments",
      points: [
        "Real-time chat via Sendbird",
        "Text and audio messages",
        "Push notifications for messages",
        "Audio playback for voice notes",
      ],
    },
    {
      title: "Notifications & Engagement",
      icon: "ti-bell",
      points: [
        "Push notifications for matches",
        "Local notifications for activity",
        "Analytics and engagement tracking",
        "iOS tracking transparency compliance",
      ],
    },
    {
      title: "Monetization & Subscriptions",
      icon: "ti-credit-card",
      points: [
        "In-app purchases for premium features",
        "Subscription handling",
        "Restore purchases support",
     "Regional currency handling",
      ],
    },
  ],

  technicalApproach: [
    {
      title: "Media-First React Native Architecture",
      description:
        "Built modular media, chat, and game components ensuring smooth performance across devices.",
    },
    {
      title: "Real-Time Communication",
      description:
        "Sendbird enabled scalable real-time chat and messaging infrastructure.",
    },
    {
      title: "Audio & Animation Handling",
      description:
        "Track Player and Lottie ensured stable audio playback and expressive UI animations.",
    },
  ],

  challengesSolved: [
    "Balanced heavy media usage with smooth UI performance",
    "Ensured stable audio recording and playback",
    "Prevented notification overload while staying responsive",
    "Integrated games without breaking chat flows",
    "Managed subscriptions without disrupting core experience",
    "Maintained trust in a sensitive social application domain",
  ],

  responsibilities: [
    "Implemented interactive matching and communication features",
    "Built audio-based profiles and chat flows",
    "Integrated game mechanics and progress indicators",
    "Managed notifications, analytics, and tracking permissions",
    "Supported in-app purchases and subscriptions",
    "Ensured production stability on iOS and Android",
  ],

  outcome: [
    {
      title: "Live on App Store & Play Store",
      description:
        "Successfully launched a global game-driven dating platform.",
      icon: "ti-mobile",
    },
    {
      title: "Higher User Engagement",
      description:
        "Increased interaction beyond traditional swipe models.",
      icon: "ti-heart",
    },
    {
      title: "Richer User Expression",
      description:
        "Enabled personality-driven connections through audio and games.",
      icon: "ti-microphone",
    },
    {
      title: "Scalable Social Foundation",
      description:
        "Built architecture ready for future social expansion.",
      icon: "ti-cloud-up",
    },
  ],

  finalNote:
    "Kupenda showcases building emotionally-driven social apps where engagement, trust, and performance are equally critical.",

  workTypeName: "Mobile Apps",
},
{
  id: "styley",
  title: "Styley",
  tagline: "AI-Powered Fashion, Photo & Lifestyle Creation Platform",

  role: "React Native Application Developer",
  client: "AI Creative Technology Platform",
  duration: "4 Months",
  techStack: "React Native (Expo), Supabase, Three.js, Reanimated",

  banner: "/assets/images/testimonial/styley_main.png",
  mockup: "/assets/images/iphone.png",
  screenshots: [
    "/assets/images/testimonial/styley/1.webp",
    "/assets/images/testimonial/styley/2.webp",
    "/assets/images/testimonial/styley/3.webp",
    "/assets/images/testimonial/styley/4.webp",
    "/assets/images/testimonial/styley/5.webp",
    "/assets/images/testimonial/styley/6.webp",
    "/assets/images/testimonial/styley/7.webp",
    "/assets/images/testimonial/styley/8.webp",
    "/assets/images/testimonial/styley/9.webp",
    "/assets/images/testimonial/styley/10.webp",
  ],
  showcaseImage: "/assets/images/testimonial/styley/showcase.webp",

  overview:
    "Styley is an AI-driven creative platform enabling users to generate fashion images, AI photos, virtual room staging, and text-to-image visuals, combined with social discovery, encrypted chat, and AI-assisted email management. It is designed as a full creative ecosystem rather than a single-purpose AI tool.",

  keyFeatures: [
    {
      title: "Fashion AI",
      icon: "ti-palette",
      points: [
        "AI-powered fashion image generation",
        "Upload photos and apply fashion styles",
        "Multiple AI models and templates",
        "Before/after comparison and editing",
      ],
    },
    {
      title: "Photo AI",
      icon: "ti-camera",
      points: [
        "AI portrait generation",
        "User model training via selfies",
        "Style selection and refinement",
        "History of AI-generated outputs",
      ],
    },
    {
      title: "Explore & Community",
      icon: "ti-comments",
      points: [
        "Browse AI-generated content",
        "Social feed with likes and comments",
        "User profiles with follow system",
        "Remix AI results",
      ],
    },
    {
      title: "Room AI & Virtual Staging",
      icon: "ti-home",
      points: [
        "AI-powered room staging",
        "Mask drawing for precision edits",
        "Multiple room themes and styles",
        "Premium staging features",
      ],
    },
    {
      title: "Text-to-Image AI",
      icon: "ti-text",
      points: [
        "Generate images from text prompts",
        "Pre-built prompt templates",
        "Custom prompt creation",
        "Multiple visual outputs",
      ],
    },
    {
      title: "Social, Chat & Mail AI",
      icon: "ti-email",
      points: [
        "Encrypted Matrix-based chat",
        "Media sharing in chat",
        "AI-powered email management",
        "Inbox categorization and search",
      ],
    },
  ],

  technicalApproach: [
    {
      title: "AI & Media-Heavy Architecture",
      description:
        "Built a modular system handling AI pipelines, media processing, and real-time social features.",
    },
    {
      title: "Advanced Animations & 3D",
      description:
        "Three.js and Reanimated delivered smooth 3D and animated UI experiences.",
    },
    {
      title: "Secure & Scalable Backend",
      description:
        "Supabase integrated for authentication, storage, and real-time data sync.",
    },
  ],

  challengesSolved: [
    "Maintained smooth UI despite AI, media, and 3D rendering",
    "Prevented performance bottlenecks in image-heavy flows",
    "Balanced feature richness with usability clarity",
    "Ensured secure authentication and encrypted communication",
    "Integrated diverse AI workflows into one cohesive experience",
    "Supported future scalability without architectural rewrites",
  ],

  responsibilities: [
    "Implemented AI-driven user flows",
    "Built media-heavy screens with smooth performance",
    "Integrated authentication, notifications, and analytics",
    "Managed camera, image, audio, and video pipelines",
    "Supported encrypted chat and social feed features",
    "Ensured stability across iOS and Web",
  ],

  outcome: [
    {
      title: "Feature-Rich AI Platform",
      description: "Delivered a multi-purpose AI creative ecosystem.",
      icon: "ti-light-bulb",
    },
    {
      title: "Multiple AI Use Cases",
      description: "Enabled fashion, photo, room, and text-to-image generation.",
      icon: "ti-image",
    },
    {
      title: "Scalable AI Foundation",
      description: "Architecture ready for future AI model expansion.",
      icon: "ti-cloud-up",
    },
    {
      title: "Premium Creative Experience",
      description: "Positioned Styley as a high-quality AI lifestyle product.",
      icon: "ti-star",
    },
  ],

  finalNote:
    "Styley represents building AI-first, media-rich mobile products where performance, polish, and scalability define success.",

  workTypeName: "Mobile Apps",
},
{
  id: "verity",
  title: "Verity",
  tagline: "Mental Wellness & Habit-Building Mobile Application",
  role: "React Native Developer",
  client: "Wellness & Mindfulness Platform",
  duration: "July 2024 – Present (18+ months)",
  techStack: "React Native, Auth0, react-native-iap, Track Player, Vision Camera, Lottie, Recharts",
  banner: "/assets/images/testimonial/verity_main.webp",
  mockup: "/assets/images/iphone.png",
  screenshots: [
    "/assets/images/testimonial/verity/1.webp",
    "/assets/images/testimonial/verity/2.webp",
    "/assets/images/testimonial/verity/3.webp",
    "/assets/images/testimonial/verity/4.webp",
    "/assets/images/testimonial/verity/5.webp",
    "/assets/images/testimonial/verity/6.webp",
    "/assets/images/testimonial/verity/7.webp",
    "/assets/images/testimonial/verity/8.webp",
    "/assets/images/testimonial/verity/9.webp",
    "/assets/images/testimonial/verity/10.webp",
    "/assets/images/testimonial/verity/11.webp",
    "/assets/images/testimonial/verity/12.webp",
  ],
  showcaseImage: "/assets/images/testimonial/verity/showcase.webp",
  overview: "Verity is a comprehensive mental wellness and habit-building mobile application designed to help users improve emotional balance, mindfulness, and daily consistency through structured activities, gamification, and guided self-reflection. The application combines mental health practices, habit tracking, reward systems, and subscription-based premium access into a single, scalable mobile experience.",
  keyFeatures: [
    {
      title: "Authentication & Security",
      icon: "ti-lock",
      points: ["Secure login and signup using Auth0", "Reliable session handling and user identity management", "Privacy-focused wellness data protection"]
    },
    {
      title: "Activities & Wellness",
      icon: "ti-heart",
      points: ["Mood Check-ins with historical tracking", "Daily Journaling with prompts and rewards", "Mindfulness Sessions and Health Assessments", "Session Agenda Recording with audio playback"]
    },
    {
      title: "Gamification System",
      icon: "ti-game",
      points: ["Scratch Card Game for activity rewards", "Spin the Wheel for random bonuses", "Streaks, points, and consistency-based incentives"]
    },
    {
      title: "Subscription & Monetization",
      icon: "ti-credit-card",
      points: ["In-app purchases using react-native-iap", "Premium plans unlocking unlimited content", "Smooth upgrade flow without disruption"]
    },
    {
      title: "Media & Visuals",
      icon: "ti-image",
      points: ["Meditation and mindfulness audio playback", "Audio recording for session notes", "Animated Lottie interactions and Interactive charts"]
    }
  ],
  technicalApproach: [
    {
      title: "Scalable Architecture",
      description: "Modular feature architecture designed for long-term scalability and maintenance."
    },
    {
      title: "Performance Optimization",
      description: "Optimized audio handling with Track Player and media-heavy components to maintain app stability."
    },
    {
      title: "State Management",
      description: "Careful state management to support complex user flows and multi-step wellness activities."
    }
  ],
  challengesSolved: [
    "Prevented performance drops despite heavy use of audio, animations, and charts",
    "Balanced gamification elements without trivializing mental wellness",
    "Ensured stable subscription behavior across app restarts and updates",
    "Maintained smooth UX for long, multi-step wellness flows",
    "Reduced friction in daily habit interactions to boost user retention"
  ],
  responsibilities: [
    "Building and integrating core application features",
    "Implementing secure authentication and subscription flows",
    "Developing interactive wellness activities and games",
    "Managing audio, media, and performance-critical components",
    "Ensuring smooth cross-platform behavior (iOS & Android)",
    "Solving real-world performance and UX challenges during scale-up"
  ],
  outcome: [
    {
      title: "Robust Wellness Platform",
      description: "Delivered a scalable and stable mental wellness product.",
      icon: "ti-mobile"
    },
    {
      title: "Consistent User Engagement",
      description: "Enabled daily habit loops and long-term retention.",
      icon: "ti-stats-up"
    },
    {
      title: "Sustainable Monetization",
      description: "Integrated subscriptions without harming user trust.",
      icon: "ti-credit-card"
    },
    {
      title: "Future-Ready Foundation",
      description: "Created a flexible base for new features and growth.",
      icon: "ti-cloud-up"
    }
  ],
  finalNote: "Verity reflects how thoughtful engineering supports real behavior change - blending psychology, technology, and business goals into one scalable product.",
  workTypeName: "Mobile Apps"
}
];

export const getProjectById = (id) =>
  PORTFOLIO_PROJECTS.find((p) => p.id === id) || null;
